import { Observable, forkJoin, from, of } from 'rxjs'
import { catchError, concatMap, map, toArray } from 'rxjs/operators'
import { LeadsService } from '../../features/crm/services/lead.service'
import { LeadService } from '../../features/crm/services/lead-info.service'
import {
    IUsersLeadsApiData,
    IUserLeadsApiUser,
    IUserLeadItemApi,
} from '../../features/crm/models/lead.model'

/**
 * Shared helper function to fetch users leads and products data
 * This avoids duplicate API calls when multiple components need the same data
 * @param userId - The user ID (RM or ZM) to fetch leads for
 * @param leadsService - Instance of LeadsService
 * @param leadInfoService - Instance of LeadService (LeadInfoService)
 * @returns Observable that emits [usersLeadsData, productMap] tuple
 */
export function fetchUsersLeadsWithProducts(
    userId: string,
    leadsService: LeadsService,
    leadInfoService: LeadService,
    academicYear?: number,
): Observable<[IUsersLeadsApiData, Map<number, string>]> {
    const usersLeads$ = leadsService.getUsersLeads(userId, undefined, undefined, academicYear)
    const products$ = leadInfoService.getActiveProducts()

    return forkJoin([usersLeads$, products$]).pipe(
        map(
            ([data, productMap]) => [data, productMap] as [IUsersLeadsApiData, Map<number, string>],
        ),
    )
}

type UsersLeadsMergeUser = Pick<
    IUserLeadsApiUser,
    'user_id' | 'user_name' | 'role_name' | 'territory_id' | 'territory_name' | 'total_leads'
> & {
    leads: IUserLeadItemApi[]
}

function mergeUsersLeadsPages(pages: IUsersLeadsApiData[]): IUsersLeadsApiData {
    const userById = new Map<string, UsersLeadsMergeUser>()

    for (const page of pages) {
        for (const user of page.user_leads || []) {
            const id = (user.user_id || '').trim()
            if (!id) continue

            const existing = userById.get(id)
            const nextLeads = (user.leads || []) as IUserLeadItemApi[]

            if (!existing) {
                userById.set(id, {
                    user_id: user.user_id,
                    user_name: user.user_name ?? null,
                    role_name: user.role_name ?? null,
                    territory_id: user.territory_id ?? null,
                    territory_name: user.territory_name ?? null,
                    total_leads: user.total_leads ?? nextLeads.length,
                    leads: [...nextLeads],
                })
                continue
            }

            existing.user_name = existing.user_name ?? user.user_name ?? null
            existing.role_name = existing.role_name ?? user.role_name ?? null
            existing.territory_id = existing.territory_id ?? user.territory_id ?? null
            existing.territory_name = existing.territory_name ?? user.territory_name ?? null
            existing.total_leads = user.total_leads ?? existing.total_leads
            existing.leads.push(...nextLeads)
        }
    }

    const mergedUsers = Array.from(userById.values())

    // Preserve pagination totals if present on the first page; page-level pagination is not meaningful after merge.
    const first = pages[0]
    const totalPages = first?.pagination?.total_pages ?? 1
    const totalLeads =
        first?.pagination?.total_leads ??
        mergedUsers.reduce((sum, u) => sum + (u.total_leads ?? u.leads.length), 0)
    const leadsPerPage = first?.pagination?.leads_per_page ?? 0

    return {
        user_leads: mergedUsers as unknown as IUserLeadsApiUser[],
        pagination: {
            current_page: 1,
            total_pages: totalPages,
            total_leads: totalLeads,
            leads_per_page: leadsPerPage,
        },
    }
}

/**
 * Fetch all pages of users-leads and merge them by user_id, along with products map.
 * Designed for dashboards that need accurate aggregates (pipeline/won) beyond page 1.
 */
export function fetchAllUsersLeadsWithProducts(
    userId: string,
    leadsService: LeadsService,
    leadInfoService: LeadService,
    academicYear?: number,
    options?: {
        /** Default is 10 to match current API default. */
        pageSize?: number
        /** Safety cap to avoid runaway requests. Default 50 pages. */
        maxPages?: number
    },
): Observable<[IUsersLeadsApiData, Map<number, string>]> {
    const products$ = leadInfoService.getActiveProducts()
    const pageSize = options?.pageSize ?? 10
    const maxPages = options?.maxPages ?? 50

    const firstPage$ = leadsService.getUsersLeads(userId, 1, pageSize, academicYear)

    const usersLeadsAll$ = firstPage$.pipe(
        concatMap((page1) => {
            const totalPagesRaw = page1?.pagination?.total_pages ?? 1
            const totalPages = Number.isFinite(totalPagesRaw)
                ? Math.max(1, Math.floor(totalPagesRaw))
                : 1
            const safeTotalPages = Math.min(totalPages, maxPages)
            const isCapped = totalPages > maxPages
            if (isCapped) {
                console.warn(
                    '[fetchAllUsersLeadsWithProducts] Capping pages to avoid excessive requests',
                    { userId, totalPages, maxPages },
                )
            }

            if (safeTotalPages <= 1) {
                return of([page1])
            }

            const pageNumbers = Array.from({ length: safeTotalPages - 1 }, (_, i) => i + 2)
            return from(pageNumbers).pipe(
                // Sequential fetch to reduce backend load
                concatMap((page) =>
                    leadsService.getUsersLeads(userId, page, pageSize, academicYear),
                ),
                toArray(),
                map((rest) => [page1, ...rest]),
            )
        }),
        map((pages) => mergeUsersLeadsPages(pages)),
        // Fallback to first-page only behavior on any failure
        catchError(() =>
            leadsService
                .getUsersLeads(userId, 1, pageSize, academicYear)
                .pipe(map((p1) => mergeUsersLeadsPages([p1]))),
        ),
    )

    return forkJoin([usersLeadsAll$, products$]).pipe(
        map(
            ([data, productMap]) => [data, productMap] as [IUsersLeadsApiData, Map<number, string>],
        ),
    )
}
